// src/components/item-builder/McqSingleModal.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Trash2 } from "lucide-react";
import { httpRequest } from "../../utils/httpRequest";
import { useTranslation } from "react-i18next";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  text_fa: "", // Passage (FA)
  text_en: "",
  options: [
    { key: "A", fa: "", en: "" },
    { key: "B", fa: "", en: "" },
    { key: "C", fa: "", en: "" },
    { key: "D", fa: "", en: "" },
  ],
  answerKey: "A",
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

// آیتم → فرم (برای حالت ادیت)
const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const opts = Array.isArray(body.options) ? body.options : [];

  const options =
    opts.length > 0
      ? opts.map((o, idx) => ({
          key: o.key || String.fromCharCode(65 + idx),
          fa: o?.label?.fa || "",
          en: o?.label?.en || "",
        }))
      : makeEmptyForm().options;

  const layout = body.layout || "top-bottom";
  const text_fa = body?.text?.fa || "";
  const text_en = body?.text?.en || "";

  const scoring = item.scoring || body.scoring || {};
  const answerKey = scoring.answerKey || options[0]?.key || "A";

  const points = item?.points ?? 1;
  const status = item?.status || "published";
  const tags = Array.isArray(item?.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    text_fa,
    text_en,
    options,
    answerKey,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function McqSingleModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm);

  // هر بار مدال باز شد / آیتم عوض شد → فرم را مقداردهی کن
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) setForm(itemToForm(item));
    else setForm(makeEmptyForm());
  }, [isOpen, isEdit, item]);

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const addOption = () => {
    const nextKey = String.fromCharCode(65 + form.options.length); // A,B,C,...
    setForm((f) => ({
      ...f,
      options: [...f.options, { key: nextKey, fa: "", en: "" }],
    }));
  };

  const removeOption = (index) => {
    if (form.options.length <= 2) return; // حداقل ۲ گزینه
    const updatedOptions = form.options.filter((_, idx) => idx !== index);

    // اگر پاسخ صحیح حذف شد، answerKey را روی اولین گزینه‌ی باقیمانده بگذار
    const removedKey = form.options[index].key;
    let nextAnswerKey = form.answerKey;
    if (removedKey === form.answerKey) {
      nextAnswerKey = updatedOptions[0]?.key || "";
    }

    setForm((f) => ({
      ...f,
      options: updatedOptions,
      answerKey: nextAnswerKey,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.stem_fa && !form.stem_en) {
      toast.error(t("mcq_single.toasts.stem_required"));
      return;
    }

    const payload = {
      kind: "mcq_single",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        options: form.options.map((o) => ({
          key: o.key,
          label: {
            fa: o.fa || undefined,
            en: o.en || undefined,
          },
        })),
        layout: form.layout,
        text: {
          fa: form.text_fa || undefined,
          en: form.text_en || undefined,
        },
      },
      scoring: {
        mode: "exact",
        answerKey: form.answerKey,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("mcq_single.toasts.updated", "سؤال با موفقیت ویرایش شد")
          : t("mcq_single.toasts.created", "سؤال با موفقیت ساخته شد")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Stem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("mcq_single.stem_fa")}
            </label>
            <CustomEditor
              value={form.stem_fa}
              onEditorChange={(content) =>
                handleEditorChange("stem_fa", content)
              }
              placeholder={t("mcq_single.stem_fa_ph")}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("mcq_single.stem_en")}
            </label>
            <CustomEditor
              value={form.stem_en}
              onEditorChange={(content) =>
                handleEditorChange("stem_en", content)
              }
              placeholder={t("mcq_single.stem_en_ph")}
            />
          </div>
        </div>

        {/* Passage */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div dir={isRtl ? "rtl" : "ltr"}>
            <label className="block text-sm font-medium mb-1">
              {t("common.passage", "Passage")} (FA)
            </label>
            <CustomEditor
              value={form.text_fa}
              onEditorChange={(content) =>
                handleEditorChange("text_fa", content)
              }
              placeholder={t("mcq_single.passage_fa_ph", "متن Passage (فارسی)")}
              directionality={isRtl ? "rtl" : "ltr"}
            />
          </div>
          <div dir="ltr">
            <label className="block text-sm font-medium mb-1">
              {t("common.passage", "Passage")} (EN)
            </label>
            <CustomEditor
              value={form.text_en}
              onEditorChange={(content) =>
                handleEditorChange("text_en", content)
              }
              placeholder={t(
                "mcq_single.passage_en_ph",
                "Passage text (English)"
              )}
            />
          </div>
        </div>

        {/* Layout Selector */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("mcq_single.layout")}
          </label>
          <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
            {["top-bottom", "bottom-top", "right-left", "left-right"].map(
              (layout) => (
                <div key={layout} className="flex items-center">
                  <input
                    type="radio"
                    id={`layout-${layout}`}
                    name="layout"
                    value={layout}
                    checked={form.layout === layout}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, layout: e.target.value }))
                    }
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                  />
                  <label
                    htmlFor={`layout-${layout}`}
                    className="ml-2 text-sm text-gray-700"
                  >
                    {t(`mcq_single.layout_${layout}`)}
                  </label>
                </div>
              )
            )}
          </div>
        </div>

        {/* Options */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            {t("mcq_single.options")}
          </label>
          {form.options.map((opt, idx) => (
            <div key={opt.key} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={opt.fa}
                onChange={(e) => {
                  const newOptions = [...form.options];
                  newOptions[idx].fa = e.target.value;
                  setForm((f) => ({ ...f, options: newOptions }));
                }}
                placeholder={t("mcq_single.option_fa_ph")}
                className="w-full border rounded-lg px-3 py-2"
              />
              <input
                type="text"
                value={opt.en}
                onChange={(e) => {
                  const newOptions = [...form.options];
                  newOptions[idx].en = e.target.value;
                  setForm((f) => ({ ...f, options: newOptions }));
                }}
                placeholder={t("mcq_single.option_en_ph")}
                className="w-full border rounded-lg px-3 py-2"
              />
              <button
                type="button"
                onClick={() => removeOption(idx)}
                className="p-2 rounded-full text-red-600 hover:bg-red-100"
                title={t("common.remove")}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addOption}
            className="text-blue-600 text-sm mt-2"
          >
            <Plus size={16} className="inline-block mr-1" />
            {t("mcq_single.add_option")}
          </button>
        </div>

        {/* Correct Answer */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            {t("mcq_single.correct_answer")}
          </label>
          <select
            value={form.answerKey}
            onChange={(e) =>
              setForm((f) => ({ ...f, answerKey: e.target.value }))
            }
            className="w-full border rounded-lg px-3 py-2"
          >
            {form.options.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.key} - {opt.fa || opt.en || t("mcq_single.no_label")}
              </option>
            ))}
          </select>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("mcq_single.points")}
            </label>
            <input
              type="number"
              min={0}
              className="w-full border rounded-lg px-3 py-2"
              value={form.points}
              onChange={(e) =>
                setForm((f) => ({ ...f, points: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("mcq_single.status")}
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="published">{t("common.published")}</option>
              <option value="draft">{t("common.draft")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("mcq_single.tags")}
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("common.tags_csv")}
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            />
          </div>
        </div>

        <ExplanationField
          valueFa={form.explanation_fa}
          valueEn={form.explanation_en}
          onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
          onChangeEn={(v) => handleEditorChange("explanation_en", v)}
          isRtl={isRtl}
        />

        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          {isEdit ? t("common.update", "به‌روزرسانی") : t("common.create")}
        </button>
      </form>
    </div>
  );
}
